import { useCallback, useEffect, useRef, useState } from 'react'

const GRACE_MS = 2000

/**
 * Tracks task IDs that were just completed on the Upcoming page. While an ID
 * sits in the returned `graceIds` set, the page keeps the completed task
 * visible so the user sees the check animation before the row disappears.
 * After `GRACE_MS` the ID is released and the task is filtered out.
 */
export function useCompletionGrace(): {
  graceIds: Set<string>
  markCompleted: (taskId: string) => void
} {
  const [graceIds, setGraceIds] = useState<Set<string>>(() => new Set())
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const markCompleted = useCallback((taskId: string) => {
    const existing = timersRef.current.get(taskId)
    if (existing) clearTimeout(existing)

    setGraceIds(prev => {
      if (prev.has(taskId)) return prev
      const next = new Set(prev)
      next.add(taskId)
      return next
    })

    const timer = setTimeout(() => {
      timersRef.current.delete(taskId)
      setGraceIds(prev => {
        if (!prev.has(taskId)) return prev
        const next = new Set(prev)
        next.delete(taskId)
        return next
      })
    }, GRACE_MS)

    timersRef.current.set(taskId, timer)
  }, [])

  useEffect(() => {
    const timers = timersRef.current
    return () => {
      timers.forEach(t => clearTimeout(t))
      timers.clear()
    }
  }, [])

  return { graceIds, markCompleted }
}
